import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Megaphone, Tag, Star, Gift, Bell, Link2, MapPin, Bot, Send, Plus, Loader2, Users,
  ExternalLink, Trophy, Award, BarChart3, MessageSquare, TrendingUp, UserCheck
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line, Legend } from "recharts";
import { API_URL } from "@/utils/apiConfig";

const CHART_COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];

const UpsellAnalyticsTab = () => {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setLoading(true);
    authAxiosRef.current.get(`/marketing/upsell-analytics?days=${period}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [period]); // period is intentional: refetch when user changes period

  if (loading) return <LoadingScreen />;
  if (!data) return <p className="text-sm text-muted-foreground text-center py-8">No hay datos de upsell disponibles.</p>;

  const sourceLabels = { popup: "Popup", sidebar: "Sidebar", checkout: "Checkout" };
  const sourceColors = { popup: "#f59e0b", sidebar: "#3b82f6", checkout: "#10b981" };

  return (
    <div className="space-y-6" data-testid="upsell-analytics-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Rendimiento de las sugerencias automaticas de upsell.</p>
        <Select value={String(period)} onValueChange={v => setPeriod(Number(v))}>
          <SelectTrigger className="w-40" data-testid="upsell-period-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Ultimos 7 dias</SelectItem>
            <SelectItem value="30">Ultimos 30 dias</SelectItem>
            <SelectItem value="90">Ultimos 90 dias</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-card border-border/50">
          <CardContent className="pt-5 text-center">
            <BarChart3 className="w-6 h-6 text-amber-500 mx-auto mb-2" />
            <p className="text-3xl font-bold" data-testid="upsell-shown">{data.shown}</p>
            <p className="text-xs text-muted-foreground mt-1">Mostrados</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="pt-5 text-center">
            <UserCheck className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-emerald-600" data-testid="upsell-accepted">{data.accepted}</p>
            <p className="text-xs text-muted-foreground mt-1">Aceptados</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="pt-5 text-center">
            <TrendingUp className="w-6 h-6 text-blue-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-blue-600" data-testid="upsell-rate">{data.conversion_rate}%</p>
            <p className="text-xs text-muted-foreground mt-1">Conversion</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="pt-5 text-center">
            <Tag className="w-6 h-6 text-rose-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-rose-600" data-testid="upsell-revenue">{data.estimated_revenue.toFixed(0)}&euro;</p>
            <p className="text-xs text-muted-foreground mt-1">Ingresos extra</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="pt-5 text-center">
            <Megaphone className="w-6 h-6 text-gray-400 mx-auto mb-2" />
            <p className="text-3xl font-bold text-gray-500" data-testid="upsell-skipped">{data.skipped}</p>
            <p className="text-xs text-muted-foreground mt-1">Ignorados</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* By Source */}
        <Card className="bg-card border-border/50">
          <CardHeader><CardTitle className="text-sm uppercase">Conversion por Canal</CardTitle></CardHeader>
          <CardContent>
            {data.by_source.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.by_source.map(s => ({ ...s, name: sourceLabels[s.source] || s.source }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="shown" name="Mostrados" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="accepted" name="Aceptados" fill="#10b981" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">Sin datos en este periodo.</p>
            )}
            <div className="grid grid-cols-3 gap-2 mt-3">
              {data.by_source.map(s => (
                <div key={s.source} className="text-center p-2 rounded-lg" style={{ backgroundColor: `${sourceColors[s.source]}10` }}>
                  <p className="text-xs font-bold uppercase" style={{ color: sourceColors[s.source] }}>{sourceLabels[s.source] || s.source}</p>
                  <p className="text-lg font-bold">{s.conversion_rate}%</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Products */}
        <Card className="bg-card border-border/50">
          <CardHeader><CardTitle className="text-sm uppercase">Top Productos Upsell</CardTitle></CardHeader>
          <CardContent>
            {data.top_products.length > 0 ? (
              <div className="space-y-2" data-testid="upsell-top-products">
                {data.top_products.map((p, i) => (
                  <div key={p.product_id} className="flex items-center justify-between p-2 rounded-lg hover:bg-secondary/50">
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-bold w-6 text-center ${i < 3 ? "text-amber-600" : "text-gray-400"}`}>{i + 1}</span>
                      <div>
                        <p className="font-medium text-sm">{p.name || "Producto"}</p>
                        <p className="text-xs text-muted-foreground">{p.count} veces aceptado</p>
                      </div>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs font-bold">+{p.revenue.toFixed(0)}&euro;</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">Sin datos de productos.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Daily Trend */}
      {data.daily.length > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader><CardTitle className="text-sm uppercase">Tendencia Diaria</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="shown" name="Mostrados" stroke="#f59e0b" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="accepted" name="Aceptados" stroke="#10b981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Insight Card */}
      <Card className="bg-emerald-50 border-emerald-200">
        <CardContent className="pt-5">
          <div className="flex items-start gap-3">
            <TrendingUp className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-emerald-800 text-sm">Resumen de Upsell</p>
              <p className="text-xs text-emerald-700 mt-1">
                {data.conversion_rate > 0
                  ? `Tu tasa de conversion es ${data.conversion_rate}%. ${data.conversion_rate >= 15 ? "Excelente rendimiento!" : data.conversion_rate >= 8 ? "Buen rendimiento, sigue optimizando." : "Hay espacio para mejorar. Prueba cambiar los productos sugeridos."} Ingresos adicionales estimados: ${data.estimated_revenue.toFixed(2)} EUR en ${period} dias.`
                  : "Aun no hay datos suficientes. Las sugerencias de upsell se rastrean automaticamente cuando los clientes interactuan con tu menu."
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export { UpsellAnalyticsTab };
